import {
  AppNode,
  CategoricalNode,
  ContinuousNode,
  DeterministicNode,
  DiscreteNode,
  ForecastNode,
  ForecastTableNode,
  LagNode,
  MixtureNode,
  NodeType,
  ScenarioNode,
  ScenarioTableNode,
  TableNode,
  isNotGroupNode,
} from "@features/builder/graph/types"
import {
  doesNodeNeedTypeUpdate,
  getChildrenNodes,
  updateNodeType,
} from "@features/builder/helpers/nodes"
import { updateParentParamsOfChildNode } from "@features/builder/helpers/update-children-node"
import { TKNode } from "@store/services/converter/types"
import logger from "@utils/logger"
import { DfCpdRow, dfCpdToRows, getNodePossibleValues, rowsToDfCpd } from "@utils/node"
import { getParentsList } from "@utils/node/getParentsList"
import { isEqual } from "lodash"
import { enqueueSnackbar } from "notistack"

export const addEdgeToNode = <TChildNode extends AppNode>(
  parent: AppNode,
  child: TChildNode,
  allNodes: AppNode[],
  linkedNode?: TKNode | undefined,
): AppNode[] => {
  // group does not have real edges, do nothing
  if (!isNotGroupNode(child)) return [child]

  const linkedNodeId = "metaNode" in parent.data ? parent.data.metaNode?.Link : undefined
  if (linkedNodeId && !linkedNode) {
    enqueueSnackbar("Something went wrong, we could not create the edge", { variant: "error" })
    throw new Error(
      "You are trying to create edge from parent node that has defined linked node. You must pass linked node",
    )
  }

  const parentNodeIds = getParentsList(child)

  // edge already exists, do nothing
  if (parentNodeIds.includes(parent.id)) return [child]

  // A Discrete/Categorical parent can declare an empty state space (e.g. Geometric with
  // ubound below loc + 1). Its states are combined with the child's existing rows, so with
  // no states there is nothing to combine — don't create the edge at all.
  if (
    usesCombinatorialExpansion(parent, child) &&
    getNodePossibleValues(parent, linkedNode).length === 0
  ) {
    logger.error(
      `Parent node ${parent.id} has no possible values, not creating edge to child node ${child.id}`,
    )

    return [child]
  }

  const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, node]))

  const updatedParentNodeIds = [...parentNodeIds, parent.id]
  if (child.type === NodeType.Table) {
    const updatedChild = structuredClone<TableNode>(child)
    updatedChild.data.originalAttribute.properties.parents_lst = updatedParentNodeIds
    allNodesUpdatedMap.set(updatedChild.id, updatedChild)
  } else if (child.type === NodeType.ForecastTable) {
    const updatedChild = structuredClone<ForecastTableNode>(child)
    updatedChild.data.originalAttribute.properties.parents_lst = updatedParentNodeIds
    allNodesUpdatedMap.set(updatedChild.id, updatedChild)
  } else if (child.type === NodeType.ScenarioTable) {
    const updatedChild = structuredClone<ScenarioTableNode>(child)
    updatedChild.data.originalAttribute.properties.parents_lst = updatedParentNodeIds
    allNodesUpdatedMap.set(updatedChild.id, updatedChild)
  } else {
    let updatedChild = structuredClone(child)
    updatedChild.data.originalNode.properties.parents_lst = updatedParentNodeIds

    if (doesNodeNeedTypeUpdate(updatedChild, allNodes)) {
      updatedChild = updateNodeType(updatedChild, allNodes) as typeof updatedChild
    }

    updatedChild = addParentRelatedParameters(parent, updatedChild, linkedNode)
    allNodesUpdatedMap.set(updatedChild.id, updatedChild)

    // recursively update children nodes
    const grandChildrenNodes = getChildrenNodes(
      updatedChild,
      Array.from(allNodesUpdatedMap.values()),
    )
    grandChildrenNodes.forEach((granChildNode) => {
      const updatedNodes = updateParentParamsOfChildNode(
        granChildNode,
        child,
        updatedChild,
        Array.from(allNodesUpdatedMap.values()),
      )
      updatedNodes.forEach((updatedNode) => allNodesUpdatedMap.set(updatedNode.id, updatedNode))
    })
  }

  const updatedNodes: AppNode[] = []

  for (const node of allNodes) {
    const updatedNode = allNodesUpdatedMap.get(node.id)
    const hasChanged = !isEqual(node, updatedNode)

    if (updatedNode && hasChanged) updatedNodes.push(updatedNode)
  }

  return updatedNodes
}

// True for the parent/child type combinations that go through
// addParentRelatedParametersWithAllPosibleCombinationsToNode below.
const usesCombinatorialExpansion = (parent: AppNode, child: AppNode): boolean =>
  (child.type === NodeType.Discrete ||
    child.type === NodeType.Categorical ||
    child.type === NodeType.Mixture) &&
  (parent.type === NodeType.Discrete || parent.type === NodeType.Categorical)

const addParentRelatedParameters = <TChildNode extends AppNode>(
  parent: AppNode,
  child: TChildNode,
  parentLinkedNode?: TKNode | undefined,
): TChildNode => {
  switch (child.type) {
    case NodeType.Continuous:
      return addParentRelatedParametersToContinuousChild(parent, child) as TChildNode
    case NodeType.Categorical:
      return addParentRelatedParametersToCategoricalChild(
        parent,
        child,
        parentLinkedNode,
      ) as TChildNode
    case NodeType.Lag:
      return addParentRelatedParametersToLagChild(parent, child) as TChildNode
    case NodeType.Mixture:
      return addParentRelatedParametersToMixtureChild(parent, child, parentLinkedNode) as TChildNode
    case NodeType.Discrete:
      return addParentRelatedParametersToDiscreteChild(
        parent,
        child,
        parentLinkedNode,
      ) as TChildNode
    default:
      return child
  }
}

const addParentRelatedParametersToDiscreteChild = (
  parent: AppNode,
  child: DiscreteNode,
  parentLinkedNode: TKNode | undefined,
): DiscreteNode => {
  switch (parent.type) {
    case NodeType.Discrete:
    case NodeType.Categorical:
      return addParentRelatedParametersWithAllPosibleCombinationsToNode(
        parent,
        child,
        parentLinkedNode,
      )
    default:
      return child
  }
}

const addParentRelatedParametersToMixtureChild = (
  parent: AppNode,
  child: MixtureNode,
  parentLinkedNode?: TKNode | undefined,
): MixtureNode => {
  switch (parent.type) {
    case NodeType.Categorical:
    case NodeType.Discrete:
      return addParentRelatedParametersWithAllPosibleCombinationsToNode(
        parent,
        child,
        parentLinkedNode,
      )
    case NodeType.Mixture:
    case NodeType.Continuous:
    case NodeType.Deterministic:
    case NodeType.Forecast:
    case NodeType.Scenario:
    case NodeType.Lag:
      return addParentRelatedParameterToMixtureChild(parent, child)
    default:
      return child
  }
}

const addParentRelatedParametersToLagChild = (parent: AppNode, child: LagNode): LagNode => {
  switch (parent.type) {
    case NodeType.Lag:
      return addLagParentRelatedParametersToLagChild(parent, child)
    default:
      return child
  }
}

const addLagParentRelatedParametersToLagChild = (parent: LagNode, child: LagNode): LagNode => {
  const updatedChild = structuredClone(child)

  const expression = updatedChild.data.originalNode.properties.df_cpd.expression?.[0] || "0"
  updatedChild.data.originalNode.properties.df_cpd.expression = [expression]
  updatedChild.data.originalNode.properties.a_sample_value = null
  updatedChild.data.metaNode["Historic Mnem"] = ""

  return updatedChild
}

const addParentRelatedParametersToCategoricalChild = (
  parent: AppNode,
  child: CategoricalNode,
  parentLinkedNode?: TKNode | undefined,
): CategoricalNode => {
  switch (parent.type) {
    case NodeType.Categorical:
    case NodeType.Discrete:
      return addParentRelatedParametersWithAllPosibleCombinationsToNode(
        parent,
        child,
        parentLinkedNode,
      )
    // case NodeType.Deterministic:
    // Connecting deterministic -> categorical is valid only if deterministic node is has "continuous" toggled of.
    // Currently in our system we always create deterministic with continuous: [true], so no need to handle it right now
    default:
      return child
  }
}

// extend it to other parent node types or child node types if the logic is the same
const addParentRelatedParametersWithAllPosibleCombinationsToNode = <
  TChild extends CategoricalNode | MixtureNode | DiscreteNode,
>(
  parent: DiscreteNode | CategoricalNode | DiscreteNode,
  child: TChild,
  parentLinkedNode?: TKNode | undefined,
): TChild => {
  const parentPossibleValues = getNodePossibleValues(parent, parentLinkedNode)
  const currentRows = dfCpdToRows(child.data.originalNode.properties.df_cpd)

  const updatedRows: DfCpdRow[] = []

  currentRows.forEach((row) => {
    parentPossibleValues.forEach((parentValue) => {
      updatedRows.push({ ...row, [parent.id]: parentValue })
    })
  })

  const updatedChild = structuredClone(child)
  updatedChild.data.originalNode.properties.df_cpd = rowsToDfCpd(updatedRows)

  return updatedChild
}

const addParentRelatedParametersToContinuousChild = (
  parent: AppNode,
  child: ContinuousNode,
): ContinuousNode => {
  switch (parent.type) {
    case NodeType.Continuous:
    case NodeType.Mixture:
    case NodeType.Deterministic:
    case NodeType.Forecast:
    case NodeType.Scenario:
    case NodeType.Lag:
      return addParentRelatedParameterToContinuousChild(parent, child)
    default:
      return child
  }
}

const addParentRelatedParameterToContinuousChild = (
  parent: ContinuousNode | MixtureNode | DeterministicNode | ForecastNode | ScenarioNode | LagNode,
  child: ContinuousNode,
): ContinuousNode => {
  const updatedChild = structuredClone(child)
  updatedChild.data.originalNode.properties.df_cpd[parent.id] = [1]

  return updatedChild
}

const addParentRelatedParameterToMixtureChild = (
  parent: ContinuousNode | MixtureNode | DeterministicNode | ScenarioNode | ForecastNode | LagNode,
  child: MixtureNode,
): MixtureNode => {
  const updatedChild = structuredClone(child)
  // all df_cpd values are arrays of same size
  const rowsCount: number =
    Object.values(updatedChild.data.originalNode.properties.df_cpd)[0]?.length ?? 0
  updatedChild.data.originalNode.properties.df_cpd[parent.id] = Array(rowsCount).fill(1)
  return updatedChild
}
